/**
 * Embedded Stripe payment (Payment Element), replacing the hosted-Checkout
 * redirect. A wide two-column dialog: a detailed, localized order summary on the
 * LEFT (what is charged, recurring terms, member pricing — for compliance) and
 * the Stripe Payment Element on the RIGHT. WE render the product copy; Stripe
 * only collects the payment method and confirms the subscription's first invoice
 * — which is also what fixes the un-localizable product names of hosted Checkout.
 *
 * TWO flows share this modal, decided by `usePrepayEnabled()`:
 *   - CH membership: `create-blood-checkout` creates a default_incomplete
 *     SUBSCRIPTION and returns its first invoice's client_secret.
 *   - DE/NL one-off: `create-booking-payment` creates a STANDALONE invoice and
 *     returns its client_secret.
 * Both return an invoice `confirmation_secret`, so everything after the invoke
 * (confirmPayment, the finalizing overlay, the webhook replay) is identical.
 *
 * Shape either way: deferred PaymentElement → elements.submit() →
 * createConfirmationToken → invoke the edge function → stripe.confirmPayment().
 *
 * paymentMethodTypes is EXPLICIT on both paths and must equal what the edge
 * function pins:
 *   - Membership: PAYMENT_METHOD_TYPES below ↔ the subscription's
 *     payment_settings in create-blood-checkout.
 *   - One-off: getPrepayPaymentMethods(market) ↔ the invoice's payment_settings
 *     in create-booking-payment.
 * Getting this wrong fails at confirm with "…collected using automatic payment
 * methods and cannot be confirmed through the API configured with
 * payment_method_types".
 *
 * Dynamic payment methods (omitting the list on both ends) is the tidier
 * integration and was tried first — it renders an EMPTY Payment Element unless
 * the account has dynamic payment methods configured for the currency, which CI
 * surfaced as a card field that never appeared.
 */
import { useEffect, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeElementLocale } from "@stripe/stripe-js";
import { Loader2, X } from "lucide-react";
import { getStripePromise } from "@/integrations/stripe/stripeClient";
import { useEmbeddedPaymentStore } from "@/stores/embeddedPaymentStore";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useBookingStore } from "@/stores/bookingStore";
import { useBloodCheckout } from "@/hooks/useBloodCheckout";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import {
  CADENCE_CONTENTS_KEY,
  getCadencePriceChf,
} from "@/utils/membershipPricing";
import {
  fullContributionCents,
  memberContributionCents,
  isScanPackage,
  memberOptionPricing,
  YEARLY_MEMBERSHIP_CENTS,
} from "@/utils/bloodMembershipPricing";
import { t as translate, resolveProductKey } from "@/locales";
import i18next from "i18next";
import { BOOKING_NS, languageMap } from "@/i18n";
import { formatPrice } from "@/components/product-step/types";
import { useMarket } from "@/hooks/useMarket";
import {
  marketPrepays,
  getCurrencyForMarket,
  getPrepayPaymentMethods,
} from "@/config/market";
import { useBundleSummary } from "@/hooks/useBundleSummary";
import { useOneOffCheckout } from "@/hooks/useOneOffCheckout";
import { listPriceCents } from "@/utils/oneOffPricing";
import { supabase } from "@/integrations/supabase/client";
import { safeLocalStorage } from "@/utils/safeStorage";
import { getUrlParam } from "@/utils/urlUtils";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import type { BookingLanguage } from "@/types/booking";
import { usePrepayEnabled } from "@/hooks/usePrepayEnabled";

/** Explicit payment methods, mirroring ahead_mono. MUST equal
 *  payment_settings.payment_method_types on the subscription in the
 *  create-blood-checkout edge function. */
const PAYMENT_METHOD_TYPES = ["card", "twint", "klarna", "paypal"];

/** Email, name and phone the user already gave us (email-capture step or the
 *  booking form, persisted as "bookingFormData"), so we can prefill Stripe +
 *  the membership doc instead of asking again. userEmail is only set by the
 *  email-capture step, so fall back to the form's email. The form stores the
 *  phone in E.164 (what Stripe's billingDetails.phone expects). */
function resolveUserDetails(bookingPayload?: Record<string, unknown> | null): {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  preferredLanguage?: string;
} {
  let firstName: string | undefined;
  let lastName: string | undefined;
  let phone: string | undefined;
  let formEmail: string | undefined;
  let preferredLanguage: string | undefined;
  const savedForm = safeLocalStorage.getItem("bookingFormData");
  if (savedForm) {
    const parsed = JSON.parse(savedForm);
    firstName = parsed.firstName || undefined;
    lastName = parsed.lastName || undefined;
    phone = parsed.phone || undefined;
    formEmail = parsed.email || undefined;
    // The "preferred language" the user picked in the booking-details form.
    // The booking submission keys off this (useBookingSubmission →
    // preferred_language: formData.preferredLanguage || language); the
    // membership checkout must do the same or the two diverge — a DE booking
    // whose header/store language stayed at the "en-US" default would email
    // the booking in German but the membership welcome in English.
    preferredLanguage = parsed.preferredLanguage || undefined;
  }
  // The form only persists to localStorage on a change it sees, so a form
  // prefilled from the URL (the mono browser tests' ?userInformation=) never
  // lands there. The live form values, via the booking payload, still do.
  const payloadEmail =
    typeof bookingPayload?.email === "string" ? bookingPayload.email : "";
  const email =
    safeLocalStorage.getItem("userEmail") ||
    formEmail ||
    payloadEmail ||
    undefined;
  return { email, firstName, lastName, phone, preferredLanguage };
}

/** LEFT column — the localized, itemised order summary (compliance detail). */
const OrderSummaryColumn = ({ language }: { language: BookingLanguage }) => {
  const { selectedAddOnDetails, selectedProductDetails } = useBookingFlow();
  const { market } = useMarket();
  const { pricing, bundle, cart } = useBloodCheckout();
  const plan = useMembershipPlanStore((s) => s.plan);
  const tiers = useMembershipTiers().tiers;
  const t = (key: string) => translate(language, key);

  const membershipCents = getCadencePriceChf(plan, tiers) * 100;

  // The scan package in a bundle cart. Without this the column listed only the
  // membership and the add-ons while the total silently included the scan, so
  // no line on a compliance surface explained the amount being charged.
  //
  // Same framing as the order summary the user just came from: the scan shows
  // its MEMBER price (never the netted one), and the membership's first year
  // reads as included rather than as CHF 299 subtracted from the scan. The
  // lines then sum to the total — 2'290 + add-ons + 0.
  const { isBundleMode, membershipFirstYearIncluded, scanOverride } =
    useBundleSummary(selectedProductDetails, selectedAddOnDetails);
  const scanPackage = isBundleMode
    ? selectedProductDetails.find(isScanPackage)
    : undefined;
  const scanMemberCents = scanPackage
    ? scanOverride(scanPackage, false)
    : undefined;

  // What checkout will actually charge. `pricing.memberTotalCents` is the blood
  // model — a sum of member prices that does not include the subscription — so
  // on a bundle it left the recurring line out of its own total: a Core cart at
  // the 4x cadence listed "Abonnement Ahead CHF 899" and then totalled
  // CHF 2'029 (1'890 + 139), understating the charge by the entire 899.
  // memberOptionPricing is the same selector the details-step card uses, so the
  // two surfaces and the charge cannot drift apart again.
  const { memberTotalCents: totalBeforeDiscountCents, memberLines } =
    memberOptionPricing({
      cart,
      bundle,
      bloodPricing: pricing,
      plan,
    });

  // Applied voucher — it discounts the invoice, so subtract it here too or the
  // displayed total wouldn't match the amount actually charged. Computed over
  // the invoice LINES: Stripe rounds a percentage coupon per line, so the same
  // rate on the sum can be a different number of cents.
  const discount = useBookingStore((s) => s.discount);
  const discountCents = voucherDiscountCents(discount, memberLines);
  const totalCents = totalBeforeDiscountCents - discountCents;

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-xl font-medium text-neutral-900">
        {t("orderSummary.becomeMember")}
      </h2>

      {/* Recurring membership line */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          {t("orderSummary.recurringAnnually")}
        </span>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-900">
              {t("membershipCadence.orderLineName")}
            </p>
            <p className="text-sm text-neutral-600">
              {t(CADENCE_CONTENTS_KEY[plan])}
            </p>
          </div>
          <span className="shrink-0 text-sm font-semibold text-neutral-900">
            {/* Always the real subscription price. This used to render CHF 0
                with the 299 struck through whenever the scan absorbed the first
                year — but the 299 is not waived, it is deducted from the scan
                (see scanOverride). Showing it as free made a reallocation look
                like a gift, and only balanced at the baseline cadence: at 4x the
                membership costs 899 and still printed 0, leaving the lines 600
                short of their own total. */}
            {formatPrice(membershipCents, market)}
          </span>
        </div>
        {/* Recurring terms directly under the membership line. It is charged
            today in every case now, so it reads the same for both scan kinds;
            the netting is explained on the scan line instead. */}
        <p className="text-xs text-neutral-500">
          {t("orderSummary.membershipChargedToday")}
        </p>
      </div>

      {/* One-time items: the scan package first, then member-priced add-ons.
          The heading drops "add-ons" once a scan is listed — the scan is the
          main purchase, not an add-on. Both keys already exist in every
          locale, so this adds no new copy. */}
      {(scanPackage || selectedAddOnDetails.length > 0) && (
        <div className="flex flex-col gap-2 border-t border-neutral-100 pt-4">
          <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            {t(
              scanPackage
                ? "bloodTracking.baseline.oneTime"
                : "orderSummary.oneTimeAddOns",
            )}
          </span>
          {scanPackage && scanMemberCents !== undefined && (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-neutral-900">
                  {resolveProductKey(language, scanPackage.name)}
                </p>
                {/* The scan carries its own blood draw, so the membership's
                    first-year test is deducted from it rather than billed twice.
                    Without this the line just looks like a bigger discount than
                    the member price actually is. */}
                {membershipFirstYearIncluded && (
                  <p className="text-xs text-neutral-500">
                    {i18next.t("orderSummary.scanAbsorbsFirstYear", {
                      lng: languageMap[language],
                      ns: BOOKING_NS,
                      amount: formatPrice(YEARLY_MEMBERSHIP_CENTS, market),
                    })}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-sm">
                {(scanPackage.default_price?.unit_amount ?? 0) >
                  scanMemberCents && (
                  <span className="mr-1 text-neutral-400 line-through">
                    {formatPrice(
                      scanPackage.default_price?.unit_amount ?? 0,
                      market,
                    )}
                  </span>
                )}
                <span className="font-medium text-neutral-900">
                  {formatPrice(scanMemberCents, market)}
                </span>
              </span>
            </div>
          )}
          {selectedAddOnDetails.map((addOn) => {
            const full = fullContributionCents(addOn);
            const member = memberContributionCents(addOn);
            return (
              <div
                key={addOn.id}
                className="flex items-start justify-between gap-3"
              >
                <p className="min-w-0 text-sm text-neutral-900">
                  {resolveProductKey(language, addOn.name)}
                </p>
                <span className="shrink-0 text-sm">
                  {full > member && (
                    <span className="mr-1 text-neutral-400 line-through">
                      {formatPrice(full, market)}
                    </span>
                  )}
                  <span className="font-medium text-neutral-900">
                    {formatPrice(member, market)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Applied voucher — discounts the first invoice. */}
      {discount && discountCents > 0 && (
        <div className="flex flex-col gap-0.5 border-t border-neutral-100 pt-4">
          <div className="flex items-center justify-between text-sm text-deep-green-600">
            <span>
              {t("orderSummary.invitationCode")} ({discount.code})
            </span>
            <span>-{formatPrice(discountCents, market)}</span>
          </div>
          {/* Only in the membership-only cart, where the voucher really does
              discount the recurring line and "renews at CHF x/year" tells the
              user this year's price isn't next year's. On a bundle the voucher
              comes off a mostly one-time cart, so hanging the membership's
              renewal price off the voucher row reads as if the CODE renews —
              and the recurring terms are already stated twice above ("Charged
              today, then yearly") and below (the renewal note). The cart and
              sidebar gate the same sub-label on `showMembership`, which is
              false in bundle mode; this is the modal's equivalent. */}
          {!isBundleMode && discount.duration && (
            <span className="text-xs text-neutral-500">
              {discount.duration === "once"
                ? i18next.t("orderSummary.discountRenewsAt", {
                    lng: languageMap[language],
                    ns: BOOKING_NS,
                    price: getCadencePriceChf(plan, tiers),
                    defaultValue: "Renews at CHF {{price}}/year",
                  })
                : t("orderSummary.discountEveryRenewal")}
            </span>
          )}
        </div>
      )}

      {/* Total due now */}
      <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
        <span className="text-base font-medium text-neutral-900">
          {t("orderSummary.totalPrice")}
        </span>
        <span className="text-lg font-semibold text-neutral-900">
          {formatPrice(totalCents, market)}
        </span>
      </div>
      {/* Compliance: restate that the membership part recurs yearly. */}
      <p className="text-xs leading-relaxed text-neutral-500">
        {t("orderSummary.renewalNote")}
      </p>
    </div>
  );
};

/**
 * LEFT column for the prepay markets (DE/NL): a flat, one-off order summary.
 * Every line is the market list price — no membership line, no member
 * strikethrough, no recurring terms, because none of that exists here.
 */
const OneOffSummaryColumn = ({ language }: { language: BookingLanguage }) => {
  const { market } = useMarket();
  const { cart, subtotalCents, discountCents, totalCents } =
    useOneOffCheckout();
  const discount = useBookingStore((s) => s.discount);
  const t = (key: string) => translate(language, key);

  return (
    <div className="flex flex-col gap-5">
      {/* testid, not copy: the Dutch details-step heading is "Controleer &
          voltooi je boeking", which a /je boeking/i matcher also hits. */}
      <h2
        data-testid="oneoff-summary-title"
        className="text-xl font-medium text-neutral-900"
      >
        {t("orderSummary.yourBooking")}
      </h2>

      <div className="flex flex-col gap-2">
        {cart.map((item) => (
          <div
            key={item.id ?? item.stripe_product_id}
            className="flex items-start justify-between gap-3"
          >
            <p className="min-w-0 text-sm text-neutral-900">
              {resolveProductKey(language, item.name)}
            </p>
            <span className="shrink-0 text-sm font-medium text-neutral-900">
              {formatPrice(listPriceCents(item), market)}
            </span>
          </div>
        ))}
      </div>

      {discount && discountCents > 0 && (
        <div className="flex items-center justify-between border-t border-neutral-100 pt-4 text-sm text-deep-green-600">
          <span>
            {t("orderSummary.invitationCode")} ({discount.code})
          </span>
          <span>-{formatPrice(discountCents, market)}</span>
        </div>
      )}

      {discountCents > 0 && (
        <div className="flex items-center justify-between text-sm text-neutral-500">
          <span>{t("orderSummary.subtotal")}</span>
          <span>{formatPrice(subtotalCents, market)}</span>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
        <span className="text-base font-medium text-neutral-900">
          {t("orderSummary.totalPrice")}
        </span>
        <span className="text-lg font-semibold text-neutral-900">
          {formatPrice(totalCents, market)}
        </span>
      </div>
      <p className="text-xs leading-relaxed text-neutral-500">
        {t("orderSummary.payNowNote")}
      </p>
    </div>
  );
};

/** RIGHT column — the Stripe Payment Element + deferred confirm flow. */
const PaymentColumn = ({
  language,
  free = false,
}: {
  language: BookingLanguage;
  // A voucher covers the whole one-off (influencer codes): nothing to collect,
  // create-booking-payment finalizes a paid €0 invoice and the webhook books.
  free?: boolean;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const setOpen = useEmbeddedPaymentStore((s) => s.setOpen);
  const setFinalizing = useEmbeddedPaymentStore((s) => s.setFinalizing);
  // Durable-booking backstop: the stable client booking id + a getter for the
  // full submit-4 booking payload (registered by BookingForm). Forwarded to
  // create-blood-checkout so it can stash the payload in pending_bookings for
  // the finalize-pending-booking webhook replay if the browser never returns.
  // getOrCreate so the id is never null at payment time (and is the SAME id the
  // session persisted → the TWINT-return submit dedupes with the webhook).
  const getOrCreateBookingId = useBookingFlowStore(
    (s) => s.getOrCreateBookingId,
  );
  const getBookingPayload = useEmbeddedPaymentStore((s) => s.getBookingPayload);
  // Applied invitation/voucher code — forwarded so the subscription is actually
  // discounted, not just the displayed total.
  const discountCode = useBookingStore((s) => s.discount?.code);
  const {
    items,
    expectedMembershipCents,
    expectedOneTimeCents,
    cadenceTierProductId,
    cadenceTierMetadataId,
    cadencePlanSlug,
  } = useBloodCheckout();
  const { market } = useMarket();
  const prepays = usePrepayEnabled();
  // Opaque, server-verified proof that this is an automated test run. Only the
  // prepay flow understands it; CH's membership checkout is untouched.
  const testPaymentToken = prepays
    ? (getUrlParam("testPaymentToken") ?? undefined)
    : undefined;
  const { items: oneOffItems, subtotalCents: oneOffSubtotal } =
    useOneOffCheckout();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = (key: string) => translate(language, key);
  // Prefill the Payment Element's contact fields (Link / receipts / billing)
  // with what the user already gave us so they don't re-type them. Stripe's
  // billingDetails takes a single `name`, so join first + last.
  const prefill = resolveUserDetails(getBookingPayload?.());
  const prefillName =
    [prefill.firstName, prefill.lastName].filter(Boolean).join(" ") ||
    undefined;
  const prefillBillingDetails = {
    ...(prefill.email ? { email: prefill.email } : {}),
    ...(prefillName ? { name: prefillName } : {}),
    ...(prefill.phone ? { phone: prefill.phone } : {}),
  };

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setError(null);
    // An automated test run carries an opaque token that create-booking-payment
    // verifies against a secret only it holds; when it accepts one there is no
    // charge, so there is also no payment method to collect, tokenize or
    // confirm. The token is only forwarded — the decision is entirely the edge
    // function's, and a rejected token throws below rather than quietly falling
    // back to charging a real card.
    const skipCardCollection = !!testPaymentToken || free;
    if (!skipCardCollection) {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? null);
        return;
      }
    }
    setLoading(true);
    try {
      // Email + name for the Stripe customer and the membership doc (mono
      // provisions + emails off it).
      const { email, firstName, lastName, preferredLanguage } =
        resolveUserDetails(getBookingPayload?.());
      let confirmationTokenId: string | undefined;
      if (!skipCardCollection) {
        const { confirmationToken, error: tokenError } =
          await stripe.createConfirmationToken({
            elements,
            params: email
              ? { payment_method_data: { billing_details: { email } } }
              : undefined,
          });
        if (tokenError) throw new Error(tokenError.message);
        confirmationTokenId = confirmationToken!.id;
      }

      // Prepay markets buy the appointment outright via create-booking-payment
      // — a standalone invoice (DE) or a bare PaymentIntent (NL). Everything
      // after this (confirmPayment, the finalizing overlay, the webhook
      // replay) is identical for every flow, because each returns a
      // PaymentIntent client secret.
      const { data, error: fnError } = prepays
        ? await supabase.functions.invoke("create-booking-payment", {
            body: {
              market,
              items: oneOffItems,
              // The pre-voucher subtotal, reconciled server-side against live
              // Stripe prices. Post-discount totals can't be compared exactly:
              // our display rounds to whole units, Stripe's coupon math doesn't.
              expectedSubtotalCents: oneOffSubtotal,
              email,
              firstName,
              lastName,
              language:
                languageMap[(preferredLanguage as BookingLanguage) || language],
              discountCode,
              idempotencyKey: crypto.randomUUID(),
              origin: window.location.origin,
              bookingId: getOrCreateBookingId(),
              bookingPayload: getBookingPayload?.() ?? undefined,
              testPaymentToken,
            },
          })
        : await supabase.functions.invoke("create-blood-checkout", {
            body: {
              email,
              firstName,
              lastName,
              // Prefer the booking-details language the user explicitly picked
              // (same source the booking submission uses: preferred_language) and
              // only fall back to the header/store language. Keeps the membership
              // welcome/receipt in the same language as the booking emails.
              // mono keys its receipt email off short codes (en/de/fr); the store
              // holds the full locale (fr-FR), which mono doesn't recognize and so
              // silently renders in English. Normalize like the rest of this file.
              language:
                languageMap[(preferredLanguage as BookingLanguage) || language],
              planSlug: cadencePlanSlug,
              items,
              membershipTierProductId: cadenceTierProductId ?? undefined,
              membershipTierMetadataId: cadenceTierMetadataId,
              expectedMembershipCents,
              expectedOneTimeCents,
              idempotencyKey: crypto.randomUUID(),
              // Drives the edge function's LIVE vs SANDBOX Stripe/provisioning
              // choice. Sent explicitly so the decision survives even if the
              // Origin header is ever stripped in transit.
              origin: window.location.origin,
              // Applied voucher → discounts the subscription's first invoice.
              discountCode,
              // Durable-booking backstop: the stable client booking id + the full
              // submit-4 booking payload, so the edge function can stash them in
              // pending_bookings and the finalize-pending-booking webhook can
              // replay the booking if the browser never returns (TWINT redirect /
              // closed tab). Both optional — the edge fn only stashes when present.
              bookingId: getOrCreateBookingId(),
              bookingPayload: getBookingPayload?.() ?? undefined,
            },
          });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      // A fully-discounted first invoice (e.g. a 100% coupon) needs no payment —
      // the subscription is already active and there's no client secret to
      // confirm. The backend flags this; we skip confirmPayment below.
      const noPaymentRequired = !!data?.noPaymentRequired;
      if (skipCardCollection && !noPaymentRequired) {
        // The edge function rejected the test token (wrong secret, wrong
        // market/email, expired, or the secret isn't set on this project). No
        // card was collected, so there is nothing to confirm — fail loudly
        // instead of silently charging.
        throw new Error(
          free
            ? "Payment unexpectedly required for a fully discounted booking"
            : "Test payment token was rejected — refusing to fall back to a real charge",
        );
      }
      if (!data?.clientSecret && !noPaymentRequired) {
        throw new Error("Could not initialize payment");
      }

      // Stash the new membership token so the booking submit can book the
      // baseline entitlement against the booking. Written before confirmPayment so
      // it survives a redirect-method (TWINT) round-trip; consumed (and cleared)
      // by useBookingSubmission.
      if (data.membershipToken) {
        safeLocalStorage.setItem(
          "pendingMembershipToken",
          data.membershipToken,
        );
      }

      // For REDIRECT methods (TWINT/Klarna/PayPal) Stripe leaves the page and
      // returns here. Send the user back to the booking app (NOT a standalone
      // page) so the flow rehydrates and loadSession routes to confirmation (the
      // booking is created async by the invoice.paid webhook). `payment_return=1`
      // is our marker; Stripe appends redirect_status + payment_intent.
      if (!noPaymentRequired) {
        const returnUrl = new URL(window.location.href);
        // Strip any params left by a PREVIOUS attempt (a failed redirect leaves
        // payment_intent / redirect_status=failed behind) so a retry's return_url
        // doesn't accumulate duplicates — duplicate redirect_status would make the
        // return read the stale "failed" instead of this attempt's "succeeded".
        [
          "payment_return",
          "redirect_status",
          "payment_intent",
          "payment_intent_client_secret",
        ].forEach((k) => returnUrl.searchParams.delete(k));
        returnUrl.searchParams.set("payment_return", "1");
        const { error: confirmError } = await stripe.confirmPayment({
          clientSecret: data.clientSecret,
          confirmParams: {
            confirmation_token: confirmationTokenId!,
            return_url: returnUrl.toString(),
          },
          redirect: "if_required",
        });
        if (confirmError) throw new Error(confirmError.message);
      }

      // Payment captured. The booking is created ASYNC by the invoice.paid
      // webhook (finalize-pending-booking), which replays the payload that
      // create-blood-checkout stashed in pending_bookings — so we do NOT submit
      // to the backend here. We DO call submitBooking(payload): despite its name
      // it only sets the confirmation page's formData (email / name / price) and
      // navigates to the ConfirmationStep — no network call. Without the formData
      // the confirmation page drops its "check your inbox" block. The modal-root
      // effect tears the overlay down once that step renders.
      // A verified test run (testMode) creates no Stripe object and stashes no
      // payload, so no webhook will ever book it. Submit it here, the same
      // { booking } body the webhook replays, so report_backend_domain routes
      // it to the test's backend.
      if (data.testMode) {
        const { error: submitError } = await supabase.functions.invoke(
          "submit-booking-with-deals-and-firebase-4",
          { body: { booking: getBookingPayload?.() } },
        );
        if (submitError) throw submitError;
      }
      setFinalizing(true);
      useBookingFlowStore
        .getState()
        .submitBooking({ ...(getBookingPayload?.() ?? {}) });
    } catch (e) {
      setError((e as Error).message);
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Heading also pushes the Payment Element's express-pay row below the
          modal's absolute ✕ close button so they don't overlap. */}
      <h3 className="text-base font-medium text-neutral-900 pr-10">
        {t(free ? "paymentInfo.title" : "paymentInfo.securePayment")}
      </h3>
      {/* Stable handle for e2e. Stripe LOCALIZES the element iframe's title
          ("Secure payment input frame" → "Sicherer Eingaberahmen für
          Zahlungen"), so tests cannot select the frame by title without
          silently breaking in every non-English market. */}
      {!free && (
        <div data-testid="stripe-payment-element">
          <PaymentElement
            options={{
              layout: "tabs",
              // One list for every market — Stripe drops entries the Element
              // isn't offering, so CH gets card→twint and NL ideal→card. The
              // first tab is the preselected one, so iDEAL (the dominant Dutch
              // method) is NL's default. CH never offers ideal, so its order
              // is unchanged.
              paymentMethodOrder: ["ideal", "card", "twint"],
              ...(Object.keys(prefillBillingDetails).length
                ? { defaultValues: { billingDetails: prefillBillingDetails } }
                : {}),
            }}
          />
        </div>
      )}

      {error && <p className="text-sm text-terracotta-600">{error}</p>}

      <button
        type="button"
        onClick={handlePay}
        disabled={loading || !stripe}
        className="w-full rounded-full bg-aubergine-800 hover:bg-aubergine-800/90 text-white py-3.5 font-semibold text-base normal-case disabled:opacity-60 transition-colors"
      >
        {loading
          ? t("orderSummary.redirecting")
          : t(free ? "bookingForm.submitDirect" : "orderSummary.pay")}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="w-full text-sm text-neutral-500 normal-case underline"
      >
        {t("common.back")}
      </button>
    </div>
  );
};

export const EmbeddedPaymentModal = () => {
  const open = useEmbeddedPaymentStore((s) => s.open);
  const setOpen = useEmbeddedPaymentStore((s) => s.setOpen);
  const finalizing = useEmbeddedPaymentStore((s) => s.finalizing);
  const setFinalizing = useEmbeddedPaymentStore((s) => s.setFinalizing);
  const currentStep = useBookingFlowStore((s) => s.currentStep);
  const { pricing, language } = useBloodCheckout();
  const { market } = useMarket();
  const prepays = usePrepayEnabled();
  const oneOff = useOneOffCheckout();
  const amountCents = prepays ? oneOff.totalCents : pricing.memberTotalCents;

  // Once the flow reaches its ConfirmationStep, tear the modal + overlay down so
  // the confetti screen is revealed (smooth hand-off, no blank gap before it).
  useEffect(() => {
    if (currentStep === "confirmation") {
      setFinalizing(false);
      setOpen(false);
    }
  }, [currentStep, setFinalizing, setOpen]);

  // Safety net: never strand the user on the spinner if the booking submit never
  // navigates (e.g. it errored). Payment + membership already succeeded.
  useEffect(() => {
    if (!finalizing) return;
    const id = window.setTimeout(() => {
      setFinalizing(false);
      setOpen(false);
    }, 25000);
    return () => window.clearTimeout(id);
  }, [finalizing, setFinalizing, setOpen]);

  if (!open) return null;

  // Payment captured → full-screen "finalizing" overlay while the booking submit
  // runs (2–3s), until the ConfirmationStep renders. Not dismissable.
  if (finalizing) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/40 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="flex flex-col items-center gap-4 rounded-2xl bg-white px-10 py-12 text-center shadow-modal">
            <Loader2 className="h-8 w-8 animate-spin text-aubergine-800" />
            <p className="max-w-xs text-base font-medium text-neutral-900">
              {translate(language, "orderSummary.finalizing")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 overflow-y-auto"
      onClick={() => setOpen(false)}
    >
      <div className="min-h-full flex items-start justify-center p-4 sm:items-center">
        <div
          className="relative w-full max-w-3xl my-8 rounded-2xl bg-white p-6 sm:p-8 shadow-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={translate(language, "common.back")}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="grid gap-8 md:grid-cols-2">
            {/* LEFT — localized order summary */}
            {prepays ? (
              <OneOffSummaryColumn language={language} />
            ) : (
              <OrderSummaryColumn language={language} />
            )}

            {/* RIGHT — Stripe payment (deferred) */}
            <div className="md:border-l md:border-neutral-100 md:pl-8">
              {amountCents > 0 ? (
                <Elements
                  stripe={getStripePromise()}
                  options={{
                    mode: "payment",
                    currency: getCurrencyForMarket(market).toLowerCase(),
                    amount: amountCents,
                    // The membership charges again every year, so the method is
                    // stored off-session. A one-off has nothing to renew.
                    ...(prepays ? {} : { setupFutureUsage: "off_session" }),
                    // Explicit on BOTH paths, and it must equal the edge
                    // function's payment_settings.payment_method_types — a
                    // mismatch throws "collected using automatic payment methods
                    // and cannot be confirmed…". Dynamic payment methods (no
                    // list) render an EMPTY Element unless the account has them
                    // configured for the currency, which is how CI found this.
                    paymentMethodTypes: prepays
                      ? [...getPrepayPaymentMethods(market)]
                      : PAYMENT_METHOD_TYPES,
                    locale: (language || "en").slice(
                      0,
                      2,
                    ) as StripeElementLocale,
                    appearance: { theme: "stripe" },
                  }}
                >
                  <PaymentColumn language={language} />
                </Elements>
              ) : prepays && oneOff.subtotalCents > 0 ? (
                // Priced cart, voucher covers all of it: book without a
                // payment method. Elements stays mounted (no amount) only so
                // PaymentColumn's Stripe hooks have their context.
                <Elements stripe={getStripePromise()}>
                  <PaymentColumn language={language} free />
                </Elements>
              ) : (
                // Stripe throws on amount <= 0 ("amount should be a positive
                // integer"), and that throw unmounts this whole modal — summary
                // included — leaving the user staring at the details step with
                // no explanation. Reachable for real: get-products deliberately
                // OMITS a product's price when the market has no tagged Stripe
                // price, so one catalog gap zeroes the cart. Refuse to mount
                // Stripe and say so instead.
                <p
                  data-testid="oneoff-price-unavailable"
                  className="text-sm text-terracotta-600"
                >
                  {translate(language, "orderSummary.priceUnavailable")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedPaymentModal;
